import { isReducible } from '$domain/events';
import type { DomainEvent, EventType, TypedEvent } from '$domain/events';

/**
 * The reducer seam.
 *
 * Derived state — today's Catches, the independence chart, the current phase,
 * trigger insights, settings — is computed by replaying the log through pure
 * functions on every start. Nothing derived is ever persisted as authoritative
 * (architecture §2.4 rule 6): the log is always the truth.
 *
 * At this data volume (a handful of events a day for twelve weeks) a full
 * replay is a few milliseconds, which is why there is no snapshot cache. A
 * cache keyed by reducer name is a second thing that can be wrong.
 */
export interface Reducer<S> {
  readonly name: string;
  readonly initial: S;
  /** The event types this reducer cares about. Everything else is ignored. */
  readonly handles: readonly EventType[];
  apply(state: S, event: TypedEvent): S;
}

/**
 * Replay a log into derived state.
 *
 * **The tolerance rule lives here, in one place.** A synced log may come from a
 * newer client carrying event types this build has never heard of, or known
 * types at a higher schema version. Those are skipped, never thrown on — that
 * is what lets an older device survive a log written by a newer one
 * (architecture §2.2), and it is why no individual reducer has to remember the
 * rule for itself.
 */
export function reduceLog<S>(reducer: Reducer<S>, events: readonly DomainEvent[]): S {
  const handled = new Set<string>(reducer.handles);
  let state = reducer.initial;

  for (const event of events) {
    if (!isReducible(event)) continue;
    if (!handled.has(event.type)) continue;
    state = reducer.apply(state, event as TypedEvent);
  }

  return state;
}

/**
 * Replay several reducers in a single pass.
 *
 * One iteration over the log rather than one per view. Worth having from the
 * start because the progress view (I4) reads five derived facts at once.
 */
export function reduceAll<T extends Record<string, Reducer<unknown>>>(
  reducers: T,
  events: readonly DomainEvent[],
): { [K in keyof T]: T[K] extends Reducer<infer S> ? S : never } {
  const entries = Object.entries(reducers);
  const state = new Map<string, unknown>(entries.map(([key, r]) => [key, r.initial]));
  const handledBy = entries.map(([key, r]) => [key, r, new Set<string>(r.handles)] as const);

  for (const event of events) {
    if (!isReducible(event)) continue;
    for (const [key, reducer, handles] of handledBy) {
      if (!handles.has(event.type)) continue;
      state.set(key, reducer.apply(state.get(key), event as TypedEvent));
    }
  }

  return Object.fromEntries(state) as { [K in keyof T]: T[K] extends Reducer<infer S> ? S : never };
}
